import { colors } from '@anyroad/theme';

export const linearizeChannel = (channel: number) => {
  const s = channel / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

export const relativeLuminance = (r: number, g: number, b: number) => {
  const R = linearizeChannel(r);
  const G = linearizeChannel(g);
  const B = linearizeChannel(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

export const compositeRgbOnWhite = (
  r: number,
  g: number,
  b: number,
  alpha: number
) => ({
  b: Math.round(b * alpha + 255 * (1 - alpha)),
  g: Math.round(g * alpha + 255 * (1 - alpha)),
  r: Math.round(r * alpha + 255 * (1 - alpha)),
});

export const parseHexChannels = (
  value: string
): { alpha: number; b: number; g: number; r: number } | null => {
  const match = /^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/iu.exec(value.trim());
  if (!match) return null;
  const [, h] = match;
  if (h.length === 3) {
    const r = Number.parseInt(h[0] + h[0], 16);
    const g = Number.parseInt(h[1] + h[1], 16);
    const b = Number.parseInt(h[2] + h[2], 16);
    return { alpha: 1, b, g, r };
  }
  if (h.length === 6) {
    return {
      alpha: 1,
      b: Number.parseInt(h.slice(4, 6), 16),
      g: Number.parseInt(h.slice(2, 4), 16),
      r: Number.parseInt(h.slice(0, 2), 16),
    };
  }
  return {
    alpha: Number.parseInt(h.slice(6, 8), 16) / 255,
    b: Number.parseInt(h.slice(4, 6), 16),
    g: Number.parseInt(h.slice(2, 4), 16),
    r: Number.parseInt(h.slice(0, 2), 16),
  };
};

export const parseRgbFunction = (
  value: string
): { alpha: number; b: number; g: number; r: number } | null => {
  const match =
    /^rgba?\(\s*([\d.]+)(%?)\s*,\s*([\d.]+)(%?)\s*,\s*([\d.]+)(%?)\s*(?:,\s*([\d.]+)\s*)?\)$/iu.exec(
      value.trim()
    );
  if (!match) return null;
  const toByte = (raw: string, isPercent: string) => {
    const n = Number.parseFloat(raw);
    if (Number.isNaN(n)) return null;
    const v = isPercent ? (n / 100) * 255 : n;
    return Math.min(255, Math.max(0, Math.round(v)));
  };
  const [, rRaw, rPct, gRaw, gPct, bRaw, bPct, alphaRaw] = match;
  const r = toByte(rRaw, rPct);
  const g = toByte(gRaw, gPct);
  const b = toByte(bRaw, bPct);
  if (r === null || g === null || b === null) return null;
  const alpha =
    alphaRaw === undefined
      ? 1
      : Math.min(1, Math.max(0, Number.parseFloat(alphaRaw)));
  if (alphaRaw !== undefined && Number.isNaN(alpha)) return null;
  return { alpha, b, g, r };
};

export const parseToRgb = (value: string) => {
  const hex = parseHexChannels(value);
  if (hex) {
    const { alpha, b, g, r } = hex;
    if (alpha >= 1) return { b, g, r };
    return compositeRgbOnWhite(r, g, b, alpha);
  }
  const rgb = parseRgbFunction(value);
  if (!rgb) return null;
  const { alpha, b, g, r } = rgb;
  if (alpha >= 1) return { b, g, r };
  return compositeRgbOnWhite(r, g, b, alpha);
};

export const foregroundOnBrandBackground = (brandColor: string) => {
  const rgb = parseToRgb(brandColor);
  if (!rgb) return colors.white;
  const lBg = relativeLuminance(rgb.r, rgb.g, rgb.b);
  return lBg > 0.5 ? colors.text.primary : colors.white;
};

type colorEnum =
  | 'destructive'
  | 'primary'
  | 'secondary'
  | 'subtle'
  | 'tertiary'
  | undefined;

const usesBrandColor = (color: colorEnum, brandColor?: string) =>
  Boolean(brandColor) && (color === 'primary' || color === 'secondary');

export const handleBackgroundColor = (
  color: colorEnum,
  brandColor?: string
) => {
  if (usesBrandColor(color, brandColor)) {
    return color === 'secondary' ? 'transparent' : brandColor!;
  }
  switch (color) {
    case 'destructive':
      return `${colors.error}`;
    case 'secondary':
      return 'transparent';
    case 'tertiary':
      return 'transparent';
    case 'subtle':
      return 'transparent';
    default:
      return `${colors.primary}`;
  }
};

export const handleBorderColor = (color: colorEnum, brandColor?: string) => {
  if (usesBrandColor(color, brandColor)) {
    return brandColor!;
  }
  switch (color) {
    case 'destructive':
      return `${colors.error}`;
    case 'secondary':
      return `${colors.primary}`;
    case 'tertiary':
      return `${colors.error}`;
    case 'subtle':
      return `${colors.text.secondary}`;
    default:
      return `${colors.primary}`;
  }
};

export const handleColor = (color: colorEnum, brandColor?: string) => {
  if (brandColor && color === 'secondary') {
    return brandColor;
  }
  if (brandColor && color === 'primary') {
    return foregroundOnBrandBackground(brandColor);
  }
  switch (color) {
    case 'destructive':
      return `${colors.white}`;
    case 'secondary':
      return `${colors.primary}`;
    case 'tertiary':
      return `${colors.error}`;
    case 'subtle':
      return `${colors.text.secondary}`;
    default:
      return `${colors.white}`;
  }
};

export const handleHoverColor = (color: colorEnum, brandColor?: string) => {
  if (usesBrandColor(color, brandColor)) {
    return color === 'primary' ? brandColor! : colors.background.light;
  }
  switch (color) {
    case 'destructive':
      return `${colors.error}`;
    case 'secondary':
      return colors.background.light;
    case 'tertiary':
      return colors.background.light;
    case 'subtle':
      return colors.background.light;
    default:
      return colors.primary;
  }
};
